import { create } from "zustand";
import { useEffect } from "react";
import clsx from "clsx";

type ConfirmKind = "danger" | "primary";

interface ConfirmRequest {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  kind?: ConfirmKind;
  onConfirm: () => Promise<void> | void;
}

interface State {
  open: boolean;
  request: ConfirmRequest | null;
  busy: boolean;
  ask: (req: ConfirmRequest) => void;
  close: () => void;
  confirm: () => Promise<void>;
}

export const useConfirm = create<State>((set, get) => ({
  open: false,
  request: null,
  busy: false,
  ask: (req) => set({ open: true, request: req, busy: false }),
  close: () => set({ open: false, request: null, busy: false }),
  confirm: async () => {
    const req = get().request;
    if (!req) return;
    set({ busy: true });
    try {
      await req.onConfirm();
      set({ open: false, request: null, busy: false });
    } catch {
      set({ busy: false });
      // The caller should toast the error.
    }
  },
}));

/**
 * Convenience wrapper: returns a function you call to ask for confirmation.
 * Example:  const confirm = useConfirmAction();
 *   await confirm({ title: "Delete?", ... });
 */
export function useConfirmAction() {
  const ask = useConfirm((s) => s.ask);
  return ask;
}

export function ConfirmDialog() {
  const { open, request, busy, close, confirm } = useConfirm();

  // Esc to cancel
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "Enter" && !busy) void confirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, close, confirm]);

  if (!open || !request) return null;

  const kind: ConfirmKind = request.kind ?? "danger";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
        onClick={close}
      />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-5 dark:bg-gray-900">
        <div className="flex items-start gap-3 mb-3">
          <div
            className={clsx(
              "h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 text-lg",
              kind === "danger" ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600",
            )}
          >
            {kind === "danger" ? "⚠" : "?"}
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{request.title}</h2>
            <p className="text-sm text-slate-600 mt-1 whitespace-pre-line dark:text-slate-300">
              {request.message}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50 dark:bg-gray-900 dark:border-gray-700 dark:text-slate-300 dark:hover:bg-gray-800"
          >
            {request.cancelLabel ?? "Cancel"}
          </button>
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={busy}
            className={clsx(
              "px-3 py-1.5 text-sm font-medium rounded-md text-white disabled:opacity-50",
              kind === "danger"
                ? "bg-red-600 hover:bg-red-700"
                : "bg-blue-600 hover:bg-blue-700",
            )}
          >
            {busy ? "…" : request.confirmLabel ?? (kind === "danger" ? "Delete" : "Confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
